import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import type { PlaceResult } from "@/lib/api/misc";
import { getKundaliTranslations, type KundaliLocale } from "./KundaliFormCard.i18n";
import { useReadingPrefill } from "./KundaliFormCard.prefill";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";
import type { KundaliField } from "@/lib/analytics-kundali";

/** Text the seeker typed into a field of the server shell before the real form mounted. */
export type KundaliInitialFieldValue = { field: KundaliField; text: string };

export function useKundaliFieldState(
  setError: Dispatch<SetStateAction<string | null>>,
  setFieldErrors: Dispatch<SetStateAction<KundaliFieldErrors>>,
  initialFieldValue?: KundaliInitialFieldValue,
  /** The field the seeker opened from the shell; saved details never fill it. */
  initialFocusField?: KundaliField
) {
  const seed = (field: KundaliField) =>
    initialFieldValue?.field === field ? initialFieldValue.text : "";
  const [name, setName] = useState(() => seed("name"));
  const [phone, setPhone] = useState(() => seed("phone"));
  const [email, setEmail] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthPlace, setBirthPlace] = useState(() => seed("birthPlace"));
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const [ambiguousSuggestion, setAmbiguousSuggestion] = useState<PlaceResult | null>(null);
  // The seeker's saved place, reused at submit while the field still shows it.
  const [rememberedPlace, setRememberedPlace] = useState<PlaceResult | null>(null);
  useReadingPrefill({
    setName,
    setBirthDate,
    setBirthTime,
    setBirthPlace,
    setSelectedPlace,
    setRememberedPlace,
    skipSavedField: initialFocusField,
  });
  const updateField = useMemo(
    () => makeFieldUpdater(setError, setFieldErrors),
    [setError, setFieldErrors]
  );
  // Stable handlers let memoized field components skip sibling-keystroke
  // rerenders while keeping error clearing beside the field state it governs.
  const updateName = useCallback(
    (value: string) => updateField("name", value, setName),
    [updateField]
  );
  const updatePhone = useCallback(
    (value: string) => updateField("phone", value, setPhone),
    [updateField]
  );
  const updateEmail = useCallback(
    (value: string) => updateField("email", value, setEmail),
    [updateField]
  );
  const updateBirthDate = useCallback(
    (value: string) => updateField("birthDate", value, setBirthDate),
    [updateField]
  );
  const updateBirthPlace = useCallback(
    (value: string) => updateField("birthPlace", value, setBirthPlace),
    [updateField]
  );
  const clearPlace = useCallback(() => {
    setSelectedPlace(null);
    setAmbiguousSuggestion(null);
    updateField("birthPlace", "", setBirthPlace);
  }, [updateField]);
  return {
    name,
    setName: updateName,
    phone,
    setPhone: updatePhone,
    email,
    setEmail: updateEmail,
    birthDate,
    setBirthDate: updateBirthDate,
    birthTime,
    setBirthTime,
    birthPlace,
    setBirthPlace: updateBirthPlace,
    selectedPlace,
    setSelectedPlace,
    ambiguousSuggestion,
    setAmbiguousSuggestion,
    rememberedPlace,
    clearPlace,
  };
}

type KundaliTranslationOverrides = {
  submitLabel?: string;
  savedNote?: string;
  savedNoteHi?: string;
  loadingLabel?: string;
  loadingLabelHi?: string;
  namePlaceholder?: string;
  placePlaceholder?: string;
  phoneReason?: string;
  phoneReasonHi?: string;
};

export function useKundaliTranslationBundle(
  language: KundaliLocale,
  overrides: KundaliTranslationOverrides
) {
  const {
    submitLabel,
    savedNote,
    savedNoteHi,
    loadingLabel,
    loadingLabelHi,
    namePlaceholder,
    placePlaceholder,
    phoneReason,
    phoneReasonHi,
  } = overrides;
  // The stable bundle lets memoized field components skip sibling-keystroke rerenders.
  return useMemo(
    () =>
      getKundaliTranslations(language, {
        submitLabel,
        savedNote: localizedOverride(language, savedNote, savedNoteHi),
        loadingLabel: localizedOverride(language, loadingLabel, loadingLabelHi),
        namePlaceholder,
        placePlaceholder,
        phoneReason: localizedOverride(language, phoneReason, phoneReasonHi),
      }),
    [
      language,
      submitLabel,
      savedNote,
      savedNoteHi,
      loadingLabel,
      loadingLabelHi,
      namePlaceholder,
      placePlaceholder,
      phoneReason,
      phoneReasonHi,
    ]
  );
}

function localizedOverride(language: KundaliLocale, en?: string, hi?: string): string | undefined {
  return language === "hi" ? (hi ?? en) : en;
}

function makeFieldUpdater(
  setError: Dispatch<SetStateAction<string | null>>,
  setFieldErrors: Dispatch<SetStateAction<KundaliFieldErrors>>
) {
  return function updateField<K extends keyof KundaliFieldErrors>(
    field: K,
    value: string,
    setter: (next: string) => void
  ) {
    setter(value);
    setError(null);
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };
}
